import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["type", "length", "pattern", "initValue", "isValid", "errorMessage", "onBlur", "onFocus", "onChange", "disabled", "theme", "iosChecker", "autoFocus", "className", "id", "style"];
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { DarkStatus } from '../statusIcon/darkStatus/darkStatus';
import { EmbersStatusIcon } from '../statusIcon/statusIcon';
import StyledCodeInput from './codeInputStyles';
import { isIpadOS, isiOS } from '../utils/string';
var TAB_KEY = 9;
var BACKSPACE_KEY = 8;
var INPUT_CACHE_KEY = 229;
export var EmbersCodeInput = function EmbersCodeInput(props) {
  var _props$type = props.type,
    type = _props$type === void 0 ? 'text' : _props$type,
    _props$length = props.length,
    length = _props$length === void 0 ? 6 : _props$length,
    _props$pattern = props.pattern,
    pattern = _props$pattern === void 0 ? /^[a-zA-Z0-9]*$/ : _props$pattern,
    initValue = props.initValue,
    _props$isValid = props.isValid,
    isValid = _props$isValid === void 0 ? true : _props$isValid,
    errorMessage = props.errorMessage,
    onBlur = props.onBlur,
    onFocus = props.onFocus,
    onChange = props.onChange,
    disabled = props.disabled,
    _props$theme = props.theme,
    theme = _props$theme === void 0 ? 'light' : _props$theme,
    iosChecker = props.iosChecker,
    _props$autoFocus = props.autoFocus,
    autoFocus = _props$autoFocus === void 0 ? false : _props$autoFocus,
    className = props.className,
    id = props.id,
    style = props.style,
    otherProps = _objectWithoutProperties(props, _excluded);
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    codeArr = _useState2[0],
    setCodeArr = _useState2[1];
  var _useState3 = useState(-1),
    _useState4 = _slicedToArray(_useState3, 2),
    clickFocus = _useState4[0],
    setClickFocus = _useState4[1];
  var inputRef = useRef([]);
  var curFilledValue = useRef('');
  // android Delete keycode compability
  var isInputCacheKey = useRef(false);
  // ios chinese keyboard compability
  var previousTimeStamp = useRef(0);
  // the ff is to handle case where the code input gets a blur when the window/tab
  // loses focus, and we want to give it the proper behavior when the window refocuses
  var clickFocusIndexOnInputBlurRef = useRef(-1);
  var prevClickFocusIndex = useRef(-1);
  var intl = useIntl();
  var isClickFocus = function isClickFocus() {
    return clickFocus !== -1;
  };
  var updateInputValue = function updateInputValue(index, value) {
    var currentCodeArr = _toConsumableArray(codeArr);
    // each field can only accommodate a single character.
    currentCodeArr[index] = value.length > 1 ? value[1] : value;
    onChange === null || onChange === void 0 || onChange(currentCodeArr.join(''));
    setCodeArr(currentCodeArr);
  };
  useEffect(function () {
    if (autoFocus) {
      setClickFocus(0);
    }
  }, [autoFocus]);

  // when the window refocus, will keep the active field in the same behavior
  useEffect(function () {
    var handleFocus = function handleFocus() {
      if (inputRef.current[clickFocusIndexOnInputBlurRef.current] === document.activeElement) {
        setClickFocus(clickFocusIndexOnInputBlurRef.current);
      }
    };
    window.addEventListener('focus', handleFocus);
    return function () {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);
  useEffect(function () {
    var codeArr = new Array(length).fill('');
    if (initValue) {
      Array.from(initValue).forEach(function (v, index) {
        return codeArr[index] = v;
      });
    }
    setCodeArr(codeArr);
  }, [initValue, length]);

  // the ff is to handle case where code input switch from active state need to trigger onBlur function
  useEffect(function () {
    if (!isClickFocus() && prevClickFocusIndex.current !== -1) {
      onBlur === null || onBlur === void 0 || onBlur();
    }
    prevClickFocusIndex.current = clickFocus;
  }, [clickFocus]);
  useEffect(function () {
    if (disabled) {
      inputRef.current.forEach(function (v) {
        v === null || v === void 0 || v.blur();
        v === null || v === void 0 || v.setAttribute('disabled', 'true');
      });
    } else {
      inputRef.current.forEach(function (v) {
        v === null || v === void 0 || v.removeAttribute('disabled');
      });
    }
  }, [disabled]);
  var onInputFocus = function onInputFocus(index, e) {
    if (iosChecker ? iosChecker() : isiOS(navigator.userAgent) || isIpadOS(navigator.userAgent)) {
      setTimeout(function () {
        var _document$activeEleme;
        (_document$activeEleme = document.activeElement) === null || _document$activeEleme === void 0 || _document$activeEleme.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 800);
    }
    onFocus === null || onFocus === void 0 || onFocus(e);
  };
  var onInputClickAndTouch = function onInputClickAndTouch(index, e) {
    e.preventDefault();
    if (codeArr[index]) {
      curFilledValue.current = codeArr[index];
      updateInputValue(index, '');
    }
  };
  var onInputBlur = function onInputBlur(index, e) {
    e.preventDefault();
    if (!codeArr[index] && curFilledValue.current) {
      updateInputValue(index, curFilledValue.current);
      curFilledValue.current = '';
    }
    if (isClickFocus()) {
      clickFocusIndexOnInputBlurRef.current = clickFocus;
    }
    index === clickFocus && setClickFocus(-1);
  };
  var onInputChange = function onInputChange(index, e) {
    var value = e.target.value;
    if (value !== '') {
      var regexResult = new RegExp(pattern);
      if (!regexResult.test(value)) {
        return;
      }

      // ios chinese input trigger twice onChange and fill the same value
      if (iosChecker ? iosChecker() : isiOS(navigator.userAgent) || isIpadOS(navigator.userAgent)) {
        var temp = previousTimeStamp.current;
        previousTimeStamp.current = e.timeStamp;
        if (e.timeStamp - temp < 150 && codeArr[index - 1 < 0 ? 0 : index - 1] === value) {
          return;
        }
      }

      // When focusing on a field with value and then replace it with a new value,
      // should clear the ref that is to store the previous value and to revert back to the original value,
      // otherwise if directly focusing on a field without value and then blur, in onInputBlur function,
      // it will be recognized as that there is already filled in with a value.
      // The temp value stored in the ref is actually not useful once you replace it with a new value.
      curFilledValue.current = '';
      updateInputValue(index, value);
      var newTarget = inputRef.current[index + 1];
      if (newTarget) {
        setTimeout(function () {
          setClickFocus(index + 1);
          newTarget.focus();
        }, 0);
      } else {
        setTimeout(function () {
          var _inputRef$current$ind;
          // the ff is to handle case where tab to last field and change value should trigger onBlur function
          if (!isClickFocus()) {
            onBlur === null || onBlur === void 0 || onBlur();
          }
          (_inputRef$current$ind = inputRef.current[index]) === null || _inputRef$current$ind === void 0 || _inputRef$current$ind.blur();
        }, 0);
      }
    }
  };
  var onHandleKeyDown = function onHandleKeyDown(index, e) {
    switch (e.which) {
      case TAB_KEY:
        {
          setClickFocus(-1);
          break;
        }
      case INPUT_CACHE_KEY:
        {
          e.preventDefault();
          isInputCacheKey.current = true;
          break;
        }
      case BACKSPACE_KEY:
        {
          e.preventDefault();
          updateInputValue(index, '');
          curFilledValue.current = '';
          setTimeout(function () {
            var _inputRef$current;
            setClickFocus(index - 1 > 0 ? index - 1 : 0);
            (_inputRef$current = inputRef.current[index - 1]) === null || _inputRef$current === void 0 || _inputRef$current.focus();
          }, 0);
          break;
        }
      default:
        break;
    }
  };
  var onHandleInput = function onHandleInput(index, e) {
    e.preventDefault();
    if (e.target.value === codeArr[index]) {
      var newTarget = inputRef.current[index + 1];
      if (newTarget) {
        setTimeout(function () {
          setClickFocus(index + 1);
          newTarget.focus();
        }, 0);
      } else {
        var _inputRef$current$ind2;
        (_inputRef$current$ind2 = inputRef.current[index]) === null || _inputRef$current$ind2 === void 0 || _inputRef$current$ind2.blur();
      }
    }
    // fix Input on Android keyCode 229 Unidentified
    // https://clark.engineering/input-on-android-229-unidentified-1d92105b9a04
    if (isInputCacheKey.current && e.nativeEvent.inputType === 'deleteContentBackward') {
      onHandleKeyDown(index, {
        which: BACKSPACE_KEY,
        preventDefault: e.preventDefault
      });
      isInputCacheKey.current = false;
    }
  };
  var onHandlePaste = function onHandlePaste(index, e) {
    e.preventDefault();
    var value = e.clipboardData.getData('Text');
    if (value) {
      var valueArr = Array.from(value).filter(function (val) {
        return new RegExp(pattern).test(val);
      });
      var updatedCodeArr = codeArr.map(function (code, i) {
        if (i >= index) {
          return valueArr[i - index] || code;
        }
        return code;
      });
      setCodeArr(updatedCodeArr);
      onChange === null || onChange === void 0 || onChange(updatedCodeArr.join(''));
      setTimeout(function () {
        var newTarget = inputRef.current[index + valueArr.length];
        if (newTarget) {
          newTarget.focus();
        } else {
          onBlur === null || onBlur === void 0 || onBlur(updatedCodeArr.join(''));
          prevClickFocusIndex.current = -1;
          setClickFocus(-1);
        }
      }, 0);
    }
  };
  var getErrorMessage = function getErrorMessage() {
    return errorMessage || intl.formatMessage({
      id: 'embers.codeInput.error.default',
      defaultMessage: 'Invalid code'
    });
  };
  return /*#__PURE__*/React.createElement(StyledCodeInput, {
    className: classNames({
      'EmbersCodeInput-root': true,
      'EmbersCodeInput-dark': theme === 'dark'
    }, className),
    id: id,
    style: style
  }, /*#__PURE__*/React.createElement("div", {
    className: "EmbersCodeInput-input"
  }, codeArr.map(function (item, index) {
    return /*#__PURE__*/React.createElement("input", _extends({
      type: type,
      autoFocus: index === 0 && autoFocus,
      ref: function ref(el) {
        return inputRef.current[index] = el;
      },
      key: index,
      maxLength: 2,
      onClick: function onClick(e) {
        return onInputClickAndTouch(index, e);
      },
      onBlur: function onBlur(e) {
        return onInputBlur(index, e);
      },
      onChange: function onChange(e) {
        return onInputChange(index, e);
      },
      onKeyDown: function onKeyDown(e) {
        return onHandleKeyDown(index, e);
      },
      onInput: function onInput(e) {
        return onHandleInput(index, e);
      },
      onPaste: function onPaste(e) {
        return onHandlePaste(index, e);
      },
      onMouseDown: function onMouseDown() {
        return setClickFocus(index);
      },
      className: classNames({
        'EmbersCodeInput-item': true,
        'EmbersCodeInput-item-clickFocus': clickFocus === index,
        'EmbersCodeInput-item-invalid': !isValid,
        'EmbersCodeInput-item-disabled': disabled,
        'EmberCodeInput-hideCursor': !!item
      }),
      value: item,
      onFocus: function onFocus(e) {
        return onInputFocus(index, e);
      },
      disabled: disabled,
      "aria-label": otherProps['aria-label'] || intl.formatMessage({
        id: 'embers.codeInput.inputLabel',
        defaultMessage: 'Type code'
      }),
      id: id ? "".concat(id, "-").concat(index) : undefined
    }, otherProps));
  })), !isValid && (theme === 'light' ? /*#__PURE__*/React.createElement(EmbersStatusIcon, {
    className: "EmbersCodeInput-status",
    type: "error",
    variant: "filled",
    size: "small"
  }, getErrorMessage()) : /*#__PURE__*/React.createElement(DarkStatus, {
    type: "error",
    className: "EmbersCodeInput-status-dark"
  }, getErrorMessage())));
};